import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';

const blankPkg = {
  name: '',
  description: '',
  default_price: 0,
  duration_minutes: 120,
  features: [],
  add_ons: [],
  is_active: true,
};

export default function AdminCustomPackages() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // package object or null
  const [featureInput, setFeatureInput] = useState('');
  const [addonName, setAddonName] = useState('');
  const [addonPrice, setAddonPrice] = useState('');
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const token = localStorage.getItem('admin_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    if (!token) { navigate('/admin/login'); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/admin/custom-packages`, { headers });
      if (res.status === 401) { navigate('/admin/login'); return; }
      setPackages(await res.json());
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const startNew = () => setEditing({ ...blankPkg });
  const startEdit = (pkg) => setEditing({
    ...blankPkg,
    ...pkg,
    features: Array.isArray(pkg.features) ? pkg.features : [],
    add_ons: Array.isArray(pkg.add_ons) ? pkg.add_ons : [],
  });
  const cancelEdit = () => { setEditing(null); setFeatureInput(''); setAddonName(''); setAddonPrice(''); };

  const duplicatePublic = (name, price, duration, features, addOns) => {
    setEditing({
      ...blankPkg,
      name: `${name} (Portal)`,
      description: `Duplicated from public: ${name}`,
      default_price: price,
      duration_minutes: duration,
      features,
      add_ons: addOns,
    });
  };

  const addFeature = () => {
    if (!featureInput.trim() || !editing) return;
    setEditing({ ...editing, features: [...(editing.features || []), featureInput.trim()] });
    setFeatureInput('');
  };
  const removeFeature = (i) => setEditing({ ...editing, features: editing.features.filter((_, idx) => idx !== i) });

  const addAddon = () => {
    if (!addonName.trim() || !editing) return;
    const price = Number(addonPrice) || 0;
    setEditing({ ...editing, add_ons: [...(editing.add_ons || []), { name: addonName.trim(), price }] });
    setAddonName(''); setAddonPrice('');
  };
  const removeAddon = (i) => setEditing({ ...editing, add_ons: editing.add_ons.filter((_, idx) => idx !== i) });

  const save = async (e) => {
    e.preventDefault();
    if (!editing.name.trim()) { alert('Package name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: editing.name.trim(),
        description: editing.description || '',
        default_price: Number(editing.default_price) || 0,
        duration_minutes: Number(editing.duration_minutes) || 120,
        features: editing.features || [],
        add_ons: (editing.add_ons || []).map((a) => ({ name: a.name, price: Number(a.price) || 0 })),
        is_active: !!editing.is_active,
      };
      const url = editing.id
        ? `${backendUrl}/api/admin/custom-packages/${editing.id}`
        : `${backendUrl}/api/admin/custom-packages`;
      const method = editing.id ? 'PATCH' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed');
      await load();
      cancelEdit();
    } catch (e) { alert(`Could not save: ${e.message}`); }
    finally { setSaving(false); }
  };

  const deletePkg = async (pkg) => {
    if (!window.confirm(`Delete package \"${pkg.name}\"? This does not remove existing bookings.`)) return;
    try {
      const res = await fetch(`${backendUrl}/api/admin/custom-packages/${pkg.id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error('Delete failed');
      await load();
    } catch (e) { alert(`Could not delete: ${e.message}`); }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Custom Packages</h2>
          <p className="text-sm text-gray-500 mt-1">Bespoke packages for portal clients. Assign them under a client's profile.</p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <button
              onClick={() => document.getElementById('dup-menu')?.classList.toggle('hidden')}
              className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg font-medium"
            >
              <i className="ri-file-copy-line mr-1"></i> Duplicate from Public▾
            </button>
            <div id="dup-menu" className="hidden absolute right-0 top-11 bg-white border border-gray-200 rounded-lg shadow-lg z-10 w-64">
              <button onClick={() => { duplicatePublic('Photography Only', 385, 30, ['Up to 30 HDR photos', 'Twilight or day shoot'], []); document.getElementById('dup-menu')?.classList.add('hidden'); }} className="block w-full text-left px-4 py-2 hover:bg-gray-50 text-sm">Photography Only — $385</button>
              <button onClick={() => { duplicatePublic('Videography Only', 660, 120, ['Cinematic property walkthrough', 'Drone footage'], []); document.getElementById('dup-menu')?.classList.add('hidden'); }} className="block w-full text-left px-4 py-2 hover:bg-gray-50 text-sm">Videography Only — $660</button>
              <button onClick={() => { duplicatePublic('Combo Package', 770, 120, ['30 HDR photos', 'Cinematic video', 'Drone shots'], []); document.getElementById('dup-menu')?.classList.add('hidden'); }} className="block w-full text-left px-4 py-2 hover:bg-gray-50 text-sm">Combo Package — $770</button>
            </div>
          </div>
          <button
            onClick={startNew}
            data-testid="new-package-btn"
            className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg font-medium"
          ><i className="ri-add-line mr-1"></i> New Package</button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading…</div>
      ) : packages.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <i className="ri-price-tag-3-line text-4xl text-gray-300"></i>
          <p className="mt-3 text-gray-500">No custom packages yet. Create one, or duplicate a public one.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {packages.map((p) => (
            <div key={p.id} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-bold text-gray-900">{p.name}</h3>
                  <div className="text-teal-600 font-semibold mt-1">A${Number(p.default_price).toFixed(2)}</div>
                  <div className="text-xs text-gray-500 mt-1">{p.duration_minutes || 120} min</div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${p.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}`}>{p.is_active ? 'Active' : 'Inactive'}</span>
              </div>
              {p.description && <p className="text-sm text-gray-600 mt-3">{p.description}</p>}
              {(p.features || []).length > 0 && (
                <ul className="mt-3 text-sm text-gray-700 space-y-1">
                  {p.features.map((f, i) => <li key={i}><i className="ri-check-line text-teal-600 mr-1"></i>{f}</li>)}
                </ul>
              )}
              {(p.add_ons || []).length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="text-xs text-gray-500 mb-1">Add-ons</div>
                  {p.add_ons.map((a, i) => (
                    <div key={i} className="text-xs text-gray-700 flex justify-between"><span>{a.name}</span><span>+A${Number(a.price).toFixed(2)}</span></div>
                  ))}
                </div>
              )}
              <div className="mt-4 flex gap-2">
                <button onClick={() => startEdit(p)} className="text-blue-600 hover:text-blue-800 text-sm font-medium">Edit</button>
                <button onClick={() => deletePkg(p)} className="text-red-600 hover:text-red-800 text-sm font-medium ml-auto">Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={cancelEdit}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-xl font-bold text-gray-900">{editing.id ? 'Edit' : 'New'} Custom Package</h3>
              <button onClick={cancelEdit} className="text-gray-400 text-2xl">×</button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Package Name *</label>
                <input required type="text" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500" data-testid="pkg-name-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea rows={2} value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Default Price (A$)</label>
                  <input type="number" step="0.01" min="0" value={editing.default_price} onChange={(e) => setEditing({ ...editing, default_price: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration (min)</label>
                  <input type="number" min="15" step="15" value={editing.duration_minutes} onChange={(e) => setEditing({ ...editing, duration_minutes: e.target.value })} className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div className="flex items-end">
                  <label className="inline-flex items-center gap-2 py-2">
                    <input type="checkbox" checked={!!editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} className="h-4 w-4" />
                    <span className="text-sm text-gray-700">Active</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Features</label>
                <div className="flex gap-2 mb-2">
                  <input type="text" value={featureInput} onChange={(e) => setFeatureInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())} placeholder="e.g. 30 HDR photos" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  <button type="button" onClick={addFeature} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium">Add</button>
                </div>
                {editing.features?.length > 0 && (
                  <ul className="space-y-1">
                    {editing.features.map((f, i) => (
                      <li key={i} className="flex justify-between bg-gray-50 px-3 py-1.5 rounded text-sm">
                        <span>{f}</span>
                        <button type="button" onClick={() => removeFeature(i)} className="text-red-600 text-xs">Remove</button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Add-ons</label>
                <div className="flex gap-2 mb-2">
                  <input type="text" value={addonName} onChange={(e) => setAddonName(e.target.value)} placeholder="e.g. Twilight photos" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  <input type="number" step="0.01" min="0" value={addonPrice} onChange={(e) => setAddonPrice(e.target.value)} placeholder="Price" className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                  <button type="button" onClick={addAddon} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium">Add</button>
                </div>
                {editing.add_ons?.length > 0 && (
                  <ul className="space-y-1">
                    {editing.add_ons.map((a, i) => (
                      <li key={i} className="flex justify-between bg-gray-50 px-3 py-1.5 rounded text-sm">
                        <span>{a.name} <span className="text-gray-500">+A${Number(a.price).toFixed(2)}</span></span>
                        <button type="button" onClick={() => removeAddon(i)} className="text-red-600 text-xs">Remove</button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="pt-2 flex gap-2">
                <button type="submit" disabled={saving} data-testid="pkg-save-btn" className="flex-1 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white py-2 rounded-lg font-medium">{saving ? 'Saving…' : 'Save Package'}</button>
                <button type="button" onClick={cancelEdit} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
